'use client'

import React, { useCallback, useMemo } from 'react'
import BackButton from '@components/BackButton'
import { Box, Typography, styled } from '@mui/material'
import { usePathname, useRouter } from 'next/navigation'
import VerticalDivider from '@components/VerticalDivider'
import Badge, { BadgeStatus } from '@components/Badge'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { ProposalDraft } from '@prisma/client'
import ProposalNavBarActions from './ProposalNavBarActions'
import { useSolana } from '@dewicats/connect-button'
import { useProposal } from '@helium/modular-governance-hooks'
import { PublicKey } from '@solana/web3.js'
import { solAddressIsValid } from '@utils/solanaUtils'
import { differenceInMinutes, isPast } from 'date-fns'

const MainBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginInlineStart: theme.spacing(2),
  flexDirection: 'row',
  height: '100%',
  alignItems: 'center',
}))

const ProposalTitle = styled(Typography)(({ theme }) => ({
  fontSize: 'var(--dewi_font_size_4)',
  textOverflow: 'ellipsis',
  '-webkit-line-clamp': 1 /* number of lines to show */,
  lineClamp: 1,
  marginInlineEnd: theme.spacing(2),
}))

const ProposalNavBar = () => {
  const { t } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()
  const { anchorProvider } = useSolana()

  const handleOnBack = useCallback(() => {
    router.replace('/dewihall/proposals')
  }, [router])

  const proposalKey = useMemo(() => {
    if (pathname.includes('review')) return
    const proposalKey = pathname.split('/').pop()
    if (solAddressIsValid(proposalKey)) {
      return proposalKey
    }
    return undefined
  }, [pathname])

  const proposal = useProposal(
    proposalKey ? new PublicKey(proposalKey) : undefined,
  )

  const fetcher = (args: any) =>
    fetch(args).then((res) => res.json() as Promise<ProposalDraft[]>)

  const { data: allDraftsForReview } = useSWR(
    '/api/getDrafts?status=[{ "status": "review" }]',
    fetcher,
  )

  const { data: draftsNotForReview } = useSWR(
    `/api/getDrafts?walletAddress=${anchorProvider?.publicKey?.toBase58()}&status=[{ "status": "review" }]`,
    fetcher,
  )

  const draft = useMemo(() => {
    if (pathname.includes('/review')) {
      return allDraftsForReview?.[0]
    }

    return draftsNotForReview?.[0]
  }, [allDraftsForReview, draftsNotForReview, pathname])

  const proposalText = useMemo(() => {
    return proposal?.info?.name
  }, [proposal])

  const badgeLabel = useMemo(() => {
    if (!proposal?.account) {
      if (draft?.status === 'review') {
        return t('components.proposalNavBar.inReview')
      }
      return t('components.proposalNavBar.draft')
    }

    if (proposal?.info?.state?.resolved) {
      return t('components.proposalNavBar.executed')
    }

    if (proposal?.info?.state?.custom?.name === 'REJECTED') {
      return t('components.proposalNavBar.rejected')
    }

    if (proposal?.info?.state.custom?.name === 'VETOED') {
      return t('components.proposalNavBar.vetoed')
    }

    const proposalLife = 7
    const unixTimestamp = Number(proposal?.info?.state?.voting?.startTs)
    const proposalEndDate = new Date(
      unixTimestamp * 1000 + proposalLife * 24 * 60 * 60 * 1000,
    )
    const now = new Date()
    const diff = differenceInMinutes(proposalEndDate, now)
    const daysLeft = Math.floor(diff / (60 * 24))
    // Get hours left after days are subtracted
    const hoursLeft = Math.floor((diff - daysLeft * 60 * 24) / 60)
    // Get minutes left after days and hours are subtracted
    const minutesLeft = diff - daysLeft * 60 * 24 - hoursLeft * 60

    // Check if proposal start date plus 7 days is in the past
    if (isPast(proposalEndDate)) {
      return t('components.proposalNavBar.votingClosed')
    }
    return t('components.proposalNavBar.active')
  }, [proposal, draft, t])

  const badgeStatus = useMemo(() => {
    if (!proposal?.account) {
      if (draft?.status === 'review') {
        return BadgeStatus.WARNING
      }
      return BadgeStatus.NEUTRAL
    }

    if (proposal?.info?.state?.custom?.name === 'EXECUTED') {
      return BadgeStatus.SUCCESS
    }

    if (proposal?.info?.state?.custom?.name === 'REJECTED') {
      return BadgeStatus.DESTRUCTIVE
    }

    if (proposal?.info?.state?.custom?.name === 'VETOED') {
      return BadgeStatus.NEUTRAL
    }

    return BadgeStatus.ACTIVE
  }, [proposal, draft])

  return (
    <MainBox>
      <BackButton onClick={handleOnBack} />
      <VerticalDivider />
      <Box
        flexDirection={{
          xs: 'column',
          sm: 'row',
        }}
        display="flex"
        flex={1}
        gap={0.25}
      >
        <ProposalTitle
          sx={{
            display: {
              xs: 'none',
              sm: 'block',
            },
          }}
          variant="h6"
        >
          {proposalText}
        </ProposalTitle>
        <Box
          display={'flex'}
          flexDirection={'row'}
          flex={1}
          alignItems="center"
          justifyContent="center"
        >
          <Badge label={badgeLabel} size="small" status={badgeStatus} />
          <Box flex={1} />
        </Box>
      </Box>
      <Box
        display={'flex'}
        flexDirection={'row'}
        alignItems="center"
        justifyContent="center"
      >
        <VerticalDivider />
        <ProposalNavBarActions />
      </Box>
    </MainBox>
  )
}

export default ProposalNavBar
